import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { Alert, Button } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { RiFilter3Fill, RiAddLine } from 'react-icons/ri';
import { SortingState } from '@tanstack/react-table';
import { toast, Bounce } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import DataTable from '@/common/tableData/DataTable';
import Paginator from '@/pages/common/paginator';
import { convertSortingToApiFormat } from '@/common/utils/convert-sorting-to-api-format';
import {
  fetchRegionsRest,
  createRegionRest,
  updateRegionRest,
  deleteRegionRest,
} from '@/services/regions/regions-service';

import { initialState, queryReducer } from './regions-query-reducer';
import { createColumns } from './regions.columns';
import { AdminRegionsResponseDto, AdminRegionInfoDto, CreateRegionDto, UpdateRegionDto } from '@repo/common-types';
import { RegionsFilter, RegionFormModal } from './components';
import StandardLayout from '@/common/layouts/StandardLayout';
import DebouncedInput from '@/components/DebounceInput';
import ConfirmModal from '@/components/ConfirmationModal/ConfirmModal';

const RegionsPage: React.FC = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [queryParams, dispatch] = useReducer(queryReducer, initialState);

  // Modal state management
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<AdminRegionInfoDto | undefined>(undefined);

  // Confirmation modal state management
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState<string | undefined>(undefined);

  const { sorting, ...rest } = queryParams;

  const { data, isLoading, error, refetch } = useQuery<AdminRegionsResponseDto>({
    queryKey: ['regionsData', queryParams],
    queryFn: () =>
      fetchRegionsRest({
        skip: rest.skip,
        take: rest.take,
        filter: rest.filter,
        sorts: convertSortingToApiFormat(sorting),
      }),
    staleTime: 60000,
  });

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      dispatch({ type: 'SET_SORTING', payload: newSorting });
    },
    [sorting],
  );

  const handleCreateRegion = useCallback(() => {
    setSelectedRegion(undefined);
    setShowRegionModal(true);
  }, []);

  const handleEditRegion = useCallback(
    (regionId: string) => {
      const region = data?.regions?.find((r) => r.id === regionId);
      if (region) {
        setSelectedRegion(region);
        setShowRegionModal(true);
      }
    },
    [data?.regions],
  );

  const handleDeleteRegion = (regionId: string) => {
    setSelectedRegionId(regionId);
    setShowConfirmModal(true);
  };

  const handleConfirmDeleteRegion = useCallback(async () => {
    try {
      if (!selectedRegionId) throw new Error('No region selected');
      const region = data?.regions?.find((r) => r.id === selectedRegionId);
      if (!region) return;

      // Check if region has active agent deals
      if (region.hasAgentDeals) {
        throw new Error('Cannot delete region with active agent deals.');
      }
      await deleteRegionRest(selectedRegionId);
      await refetch();

      toast.success(`Region "${region.name}" deleted successfully.`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
        transition: Bounce,
      });
    } catch (error) {
      console.error('Error deleting region:', error);

      const specificError = error instanceof Error ? error.message : '';
      const title = 'Failed to delete region.';
      const errorMessage = specificError ? `${title}\n\n${specificError}` : `${title} Please try again.`;

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'dark',
        transition: Bounce,
      });
    } finally {
      setShowConfirmModal(false);
      setSelectedRegionId(undefined);
    }
  }, [data?.regions, selectedRegionId, refetch]);

  const handlePaginationChange = useCallback((skip: number, take: number) => {
    dispatch({ type: 'SET_PAGINATION', payload: { skip, take } });
  }, []);

  const handleSearch = useCallback((value: string | number) => {
    dispatch({ type: 'SET_SEARCH', payload: value.toString() });
  }, []);

  // Check if filters differ from initial state (empty object)
  const hasActiveFilters = useMemo(() => {
    const currentFilter = queryParams.filter || {};
    return (
      Object.keys(currentFilter).length > 0 &&
      Object.values(currentFilter).some((value) => value !== undefined && value !== '')
    );
  }, [queryParams.filter]);

  const handleModalClose = useCallback(() => {
    setShowRegionModal(false);
    setSelectedRegion(undefined);
  }, []);

  const handleFormSubmit = useCallback(
    async (regionData: CreateRegionDto | UpdateRegionDto) => {
      try {
        if (selectedRegion) {
          await updateRegionRest(selectedRegion.id, regionData as UpdateRegionDto);
          toast.success('Region updated successfully.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'dark',
            transition: Bounce,
          });
        } else {
          await createRegionRest(regionData as CreateRegionDto);
          toast.success('Region created successfully.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: 'dark',
            transition: Bounce,
          });
        }

        await refetch();
        handleModalClose();
      } catch (error) {
        console.error('Error submitting region:', error);

        const specificError = error instanceof Error ? error.message : '';
        const action = selectedRegion ? 'update' : 'create';
        const title = `Failed to ${action} region.`;
        const errorMessage = specificError ? `${title}\n\n${specificError}` : `${title} Please try again.`;

        toast.error(errorMessage, {
          position: 'top-right',
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'dark',
          transition: Bounce,
        });
      }
    },
    [selectedRegion, refetch, handleModalClose],
  );

  const columns = useMemo(
    () => createColumns(handleEditRegion, handleDeleteRegion),
    [handleEditRegion, handleDeleteRegion],
  );

  return (
    <StandardLayout
      pageTitle="Regions Management"
      headerButton={
        <Button color="primary" className="d-flex align-items-center gap-2" onClick={handleCreateRegion}>
          <RiAddLine />
          Add New Region
        </Button>
      }
    >
      {error ? (
        <Alert color="danger">Error when fetching regions data</Alert>
      ) : (
        <>
          <div className="w-100 d-flex justify-content-between align-items-center mb-4 gap-2">
            <div className="d-flex align-items-center justify-content-start gap-2">
              <DebouncedInput
                className="form-control w-auto"
                placeholder="Search"
                isLoading={false}
                onChange={handleSearch}
              />
              <Button onClick={() => setShowFilterModal(true)} type="button" outline color="dark">
                <span className="d-flex align-items-center gap-2">
                  <RiFilter3Fill size={20} />
                  Filter
                </span>
              </Button>
              {hasActiveFilters && (
                <Button type="button" outline color="dark" onClick={() => dispatch({ type: 'RESET_FILTERS' })}>
                  Clear Filters
                </Button>
              )}
            </div>
          </div>

          <DataTable
            data={data?.regions || []}
            columns={columns}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            enableSorting={true}
          />

          <Paginator
            take={queryParams.take || 25}
            skip={queryParams.skip || 0}
            showing={data?.regions?.length || 0}
            totalCount={data?.totalCount || 0}
            setTake={(take) => handlePaginationChange(queryParams.skip || 0, take)}
            setSkip={(skip) => handlePaginationChange(skip, queryParams.take || 25)}
          />

          {/* Filter Modal */}
          {showFilterModal && (
            <RegionsFilter
              onClose={() => setShowFilterModal(false)}
              onApplyFilters={(filters) => {
                dispatch({ type: 'SET_FILTERS', payload: filters });
                setShowFilterModal(false);
              }}
              currentFilters={queryParams.filter}
            />
          )}

          {/* Region Form Modal */}
          {showRegionModal && (
            <RegionFormModal
              region={selectedRegion}
              onClose={handleModalClose}
              onSubmit={handleFormSubmit}
              isLoading={false} // TODO: Add loading state management
            />
          )}

          {showConfirmModal && (
            <ConfirmModal
              onClose={() => {
                setShowConfirmModal(false);
                setSelectedRegionId(undefined);
              }}
              onConfirm={handleConfirmDeleteRegion}
              title="Confirm"
              message="Are you sure you want to delete this region?"
            />
          )}
        </>
      )}
    </StandardLayout>
  );
};

export default RegionsPage;
